import React from "react";
import { useTranslation } from 'react-i18next';
import { Box, Columns, Column, Stack, TextField, IconArrow, Checkbox, Textarea, Dropdown, Drawer, IconSearch, Card } from "braid-design-system";
import CustomerInfoTable from "./CustomerInfoTable";
import { ChequeBookResponse, getChequeBooks } from "../services/glAccountService";
import ApproveMobileAppCustomer from "../pages/ApproveMobileAppCustomer";

export interface AccountTypeInfo {
    cashMargin: number;
    interestGap: number;
    minPeriod: number;
    maxPeriod: number;
}

interface CustomerSelectionSectionProps {
    customerNumber: string;
    setCustomerNumber: (val: string) => void;
    inputRef: React.RefObject<HTMLInputElement | null>;
    fetchCustomerInfo: () => void;
    isNonCustomer?: boolean;
    setIsNonCustomer?: (val: boolean) => void;
    nonCustomerName?: string;
    setNonCustomerName?: (val: string) => void;
    isCheque?: boolean;
    setIsCheque?: (val: boolean) => void;
    chequeNumber?: string | null;
    setChequeNumber?: (val: string) => void;
    bankName?: number | null;
    setBankName?: (val: number | null) => void;
    isNote?: boolean;
    setIsNote?: (val: boolean) => void;
    note?: string;
    setNote?: (val: string) => void;
    customerInfo: any;
    handleCusomerClick: () => void;
    feildDisable?: boolean;
    showChequeBooks?: boolean;
    showNotes?: boolean;
    showExtras?: boolean;
    hideAddButton?: boolean;
    hideActionColumn?: boolean;
    showNonCustomerToggle?: boolean;
}

const CustomerSelectionSection: React.FC<CustomerSelectionSectionProps> = ({
    customerNumber,
    setCustomerNumber,
    inputRef,
    fetchCustomerInfo,
    isNonCustomer = false,
    setIsNonCustomer,
    nonCustomerName = "",
    setNonCustomerName,
    isCheque = false,
    setIsCheque,
    chequeNumber = null,
    setChequeNumber,
    bankName = null,
    setBankName,
    isNote = false,
    setIsNote,
    note = "",
    setNote,
    customerInfo,
    handleCusomerClick,
    feildDisable = false,
    showChequeBooks = true,
    showNotes = true,
    showExtras = true,
    hideAddButton = true,
    showNonCustomerToggle = false,
}) => {
    const [bankOptions, setBankOptions] = React.useState<ChequeBookResponse[]>([]);
    const [isApprovedCustomerDrawerOpen, setIsApprovedCustomerDrawerOpen] = React.useState(false);

    React.useEffect(() => {
        // Fetch bank options when component mounts
        getChequeBooks().then(options => setBankOptions(options));

        //eslint-disable-next-line no-console
        console.log("CustomerSelectionSection mounted, fetched bank options", customerInfo);
    }, []);

    const { t } = useTranslation();

    React.useEffect(() => {
        const element = inputRef.current;
        if (!element) return;
        
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && customerNumber.trim()) {
                fetchCustomerInfo();
            }
        };
        
        element.addEventListener('keydown', handleKeyDown);
        return () => element.removeEventListener('keydown', handleKeyDown);
    }, [customerNumber, fetchCustomerInfo]);


    return (
        <Box paddingTop={"large"}>
            <Columns space={"large"} collapseBelow="desktop">
                <Column width="1/2">
                    <Box className="premium-card-shadow" style={{ height: '100%' }}>
                        <Card>
                        <Box style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
                        <Stack space={"medium"}>
                            {showNonCustomerToggle && (
                                <Checkbox
                                    checked={isNonCustomer}
                                    onChange={() => setIsNonCustomer?.(!isNonCustomer)}
                                    label={t('component.customer_selection.checkbox.non_customer', 'Non-Customer')}
                                />
                            )}
                            <Columns space="medium" alignY="bottom" collapseBelow="desktop">
                                {showNonCustomerToggle && isNonCustomer ? (
                                    <Column>
                                        <TextField
                                            aria-labelledby="NonCustomerNameLabel"
                                            onChange={(e) => setNonCustomerName?.(e.currentTarget.value)}
                                            value={nonCustomerName}
                                            onClear={() => setNonCustomerName?.("")}
                                            placeholder={t('component.customer_selection.placeholder.non_customer_name', 'Non-Customer Name')}
                                        />
                                    </Column>
                                ) : (
                                    <>
                                        <Column>
                                            <TextField
                                                aria-labelledby="CustomerNumberLabel"
                                                onChange={(e) => setCustomerNumber(e.currentTarget.value)}
                                                value={customerNumber}
                                                onClear={() => setCustomerNumber("")}
                                                placeholder={t('component.customer_selection.placeholder.customer_number', 'Customer Number')}
                                                ref={inputRef}
                                            />
                                        </Column>
                                        <Column width="content" >
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', height: '100%' }}>
                                                <button
                                                    type="button"
                                                    className="premium-blue-button"
                                                    onClick={() => setIsApprovedCustomerDrawerOpen(true)}
                                                    style={{ whiteSpace: 'nowrap' }}
                                                >
                                                    <IconSearch /> {t('component.customer_selection.button.search', 'Search')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="premium-blue-button-ghost"
                                                    onClick={
                                                        () => {
                                                            if (customerNumber.trim()) {
                                                                fetchCustomerInfo();
                                                            } else {
                                                                //eslint-disable-next-line no-console
                                                                console.log("Customer number is empty, cannot fetch customer info");
                                                            }
                                                        }
                                                    }
                                                    style={{ whiteSpace: 'nowrap' }}
                                                >
                                                    {t('component.customer_selection.button.next', 'Next')} <IconArrow direction="right" />
                                                </button>
                                            </div>
                                        </Column>
                                    </>
                                )}
                            </Columns>
                            
                            {showExtras ? (
                                <Stack space="medium">
                                    {showChequeBooks && (
                                        <Stack space="small">
                                            <Checkbox
                                                checked={isCheque}
                                                onChange={() => {
                                                    const newValue = !isCheque;
                                                    setIsCheque?.(newValue);

                                                    // Clear cheque number and bank name if unchecking
                                                    if (!newValue) {
                                                        setChequeNumber?.("");
                                                        setBankName?.(null);
                                                    }
                                                }}
                                                label={t('component.customer_selection.checkbox.cheque', 'Cheque')}
                                                disabled={feildDisable}
                                            />
                                            <Box hidden={!isCheque} paddingTop="small">
                                                <Columns space="large" alignY="bottom">
                                                    <Column width="1/2">
                                                        <TextField
                                                            aria-labelledby="ChequeNumberLabel"
                                                            onChange={(e) => setChequeNumber?.(e.currentTarget.value)}
                                                            value={chequeNumber || ""}
                                                            onClear={() => setChequeNumber?.("")}
                                                            placeholder={t('component.customer_selection.placeholder.cheque_number', 'Cheque Number')}
                                                            disabled={feildDisable}
                                                        />
                                                    </Column>
                                                    <Column width="1/2">
                                                        <Dropdown
                                                            id="bankNameSelect"
                                                            aria-labelledby="BankNameLabel"
                                                            placeholder={t('component.customer_selection.placeholder.select_bank', 'Select Bank Name')}
                                                            value={bankName || ""}
                                                            onChange={(e) => setBankName?.(Number(e.currentTarget.value))}
                                                            disabled={feildDisable}
                                                        >
                                                            {bankOptions.map((option) => (
                                                                <option key={option.id} value={String(option.id)}>
                                                                    {option.name}
                                                                </option>
                                                            ))}
                                                        </Dropdown>
                                                    </Column>
                                                </Columns>
                                            </Box>
                                        </Stack>
                                    )}
                                    {showNotes && (
                                        <Stack space="xsmall">
                                            <Checkbox
                                                checked={isNote}
                                                onChange={() => setIsNote?.(!isNote)}
                                                label={t('component.customer_selection.checkbox.note', 'Note')}
                                                disabled={feildDisable}
                                            />
                                            <Box hidden={!isNote} paddingTop="small">
                                                <Textarea
                                                    aria-labelledby="NoteLabel"
                                                    onChange={(e) => setNote?.(e.currentTarget.value)}
                                                    value={note}
                                                    placeholder={t('component.customer_selection.placeholder.enter_note', 'Enter your note here')}
                                                    disabled={feildDisable}
                                                />
                                            </Box>
                                        </Stack>
                                    )}
                                </Stack>
                            ) : null}
                        </Stack>
                    </Box>
                </Card>
            </Box>
        </Column>
                <Column width="1/2">
                    <Box position="sticky" top={0}>
                        {customerInfo !== null && customerInfo !== undefined && (
                            <CustomerInfoTable customerInfo={customerInfo} onCustomerClick={handleCusomerClick} />
                        )}
                    </Box>
                </Column>
            </Columns>
            <Drawer
                title={t('component.customer_selection.drawer.search_customer', 'Search Customers')}
                open={isApprovedCustomerDrawerOpen}
                onClose={() => setIsApprovedCustomerDrawerOpen(false)}
                width="large"
            >
                <Box id="mobile-customer-search-drawer-content">
                    <ApproveMobileAppCustomer
                        onSelect={(number) => {
                            setCustomerNumber(number);
                            setIsApprovedCustomerDrawerOpen(false);
                        }}
                        hideAddButton={hideAddButton}
                        hideActionColumn={false}
                    />
                </Box>
            </Drawer>
        </Box>
    )
};

export default CustomerSelectionSection;
